import { useContext, useMemo } from 'react';
import SortOption from '~/constants/common/SortOption';
import AppliedFiltersContext from '~/context/common/AppliedFiltersContext';
import { useIsTradeInApplied } from '~/context/common/TradeContext';
import { useUserLocation } from '~/context/common/UserContext';
import { useDealershipId } from '~/context/dealership/StaticContext';
import { useVerboseLogging } from '~/context/srp/StaticContext';
import type {
  InventoryRequest,
  InventoryRequestAnalytics,
  RequestedFeature
} from '~/contracts/common';
import { isDefined } from '~/utilities/array';
import BrowserCookies from '~/utilities/BrowserCookies';

const baseRequestedFeatures: RequestedFeature[] = [
  'EarliestAcquisitionBoosting',
  'ExcludeFacetData',
  'HideImpossibleCombos',
  'LoanTermPricing',
  'LocationBasedPrefiltering',
  'Personalization'
];

const baseLimitedRequestFeatures: RequestedFeature[] = [
  'ExcludeFacetData',
  'HideImpossibleCombos',
  'LocationBasedPrefiltering'
];

const dummyAnalyticsData: InventoryRequestAnalytics = {
  browser: '',
  clientId: '',
  deviceName: '',
  isBot: false,
  isFirstActiveSearchSession: false,
  isMobileDevice: false,
  previousSearchRequestId: '',
  referrer: '',
  searchRequestId: '',
  searchSessionId: '',
  utmParams: {}
};

type Options = {
  includePageNumber: boolean;
  limitRequestedFeatures: boolean;
  shouldGetConditionalTiles: boolean;
};

const defaultOptions: Options = {
  includePageNumber: true,
  limitRequestedFeatures: false,
  shouldGetConditionalTiles: false
};

/**
 * Generates an inventory request object, minus the analytics object.
 *
 * A dummy analytics object will be included, but if you need a complete object
 * then it should be spread into the request object before sending. You can
 * generate the object using the function returned from the
 * `useGenerateAnalyticsData` hook.
 *
 * The complete object is not included because we need it to be memoized, but we
 * also need it to have the latest values from the context. We don't want
 * changes to the analytics object to trigger a new request (thus the
 * memoization), but we do need the latest values to be included in the request.
 * To achieve this, the analytics object has been separated from the request and
 * made into a function that returns the object as needed.
 */
export default function useGenerateInventoryRequest(
  pageSize: number,
  options: Partial<Options> = {}
): {
  request: InventoryRequest;
  conditionalRequestedFeatures: RequestedFeature[];
} {
  const opts: Options = { ...defaultOptions, ...options };

  const { appliedFilters, pageNumber, sortBy } = useContext(
    AppliedFiltersContext
  );
  const dealershipId = useDealershipId();
  const { zip = '' } = useUserLocation();
  const shouldApplySprinkles = sortBy === SortOption.MostPopular;
  const verboseLoggingEnabled = useVerboseLogging();

  const requestedFeatures = useMemo<RequestedFeature[]>(
    () =>
      (opts.limitRequestedFeatures
        ? baseLimitedRequestFeatures
        : ([] as Array<RequestedFeature | undefined>)
            .concat(baseRequestedFeatures)
            .concat(
              dealershipId ? 'DealershipConditionalTiles' : 'ConditionalTiles'
            )
            .concat(verboseLoggingEnabled ? 'ApplyVerboseLogging' : undefined)
            .concat(shouldApplySprinkles ? 'Sprinkles' : undefined)
      ).filter(isDefined),
    [
      dealershipId,
      opts.limitRequestedFeatures,
      shouldApplySprinkles,
      verboseLoggingEnabled
    ]
  );

  const isTradeInApplied = useIsTradeInApplied();
  const conditionalRequestedFeatures = useMemo<RequestedFeature[]>(
    () => [isTradeInApplied ? 'ApplyTradeIn' : 'FetchTradeIn'],
    [isTradeInApplied]
  );

  const page = opts.includePageNumber ? pageNumber : 1;

  const request = useMemo<InventoryRequest>(
    () => ({
      analyticsData: dummyAnalyticsData,
      browserCookieId: BrowserCookies.get('BrowserCookieId') ?? '',
      dealershipId,
      filters: appliedFilters,
      pagination: { page, pageSize },
      requestedFeatures,
      sortBy,
      zip5: zip
    }),
    [
      appliedFilters,
      dealershipId,
      page,
      pageSize,
      requestedFeatures,
      sortBy,
      zip
    ]
  );

  return {
    request,
    conditionalRequestedFeatures
  };
}
